import { Button } from "components/shared/panda/Button"
import { parseBackground, useSizing } from "lib/helpers"
import { urlForImage } from "lib/sanity.image"
import { PricingShowcase } from "schemas"
import { css } from "styled-system/css"
import { Container, Grid, HStack, styled, VStack } from "styled-system/jsx"

type Props = PricingShowcase

function PricingShowcase({
  title,
  price,
  priceUnit,
  priceTip,
  features,
  image,
  cta,
  position,

  sizingTop,
  sizingBottom,
  backgroundColor1,
  backgroundColor2,
  backgroundStyle,
}: Props) {
  return (
    <styled.div
      style={
        {
          background: parseBackground(
            backgroundColor1,
            backgroundColor2,
            backgroundStyle
          ),
          "--pt": useSizing(sizingTop),
          "--pb": useSizing(sizingBottom),
        } as React.CSSProperties
      }
      css={{
        // height: "60vh",
        py: "64px",
        pt: "var(--pt, 72px)",
        pb: "var(--pb, 72px)",
        bgColor: "rgba(136, 156, 255, 0.29)",
      }}>
      <Container>
        <Grid
          columns={{}}
          gap="52px"
          gridTemplateColumns={{ base: "1fr", lg: "1.2fr 1fr" }}>
          <styled.div
            minH="500px"
            position="relative"
            order={{
              base: "1",
              lg: position === "left" ? "-1" : "1",
            }}>
            {/* {image?.asset && <styled.img src={urlForImage(image).url()} />} */}
            {image?.asset && (
              <img
                className={css({
                  objectFit: "contain",
                  objectPosition: "top center",
                  width: "100%",
                  height: "100%",
                })}
                alt={image.alt || ""}
                src={urlForImage(image).url()}
                // fill
              />
            )}
          </styled.div>
          <VStack gap="32px" alignItems="start" justifyContent="center">
            <styled.h2
              css={{
                color: "#1800D9",
                fontFamily: "heading",
                fontSize: "60px",
                fontWeight: "700",
                lineHeight: 1,
                letterSpacing: "-1.14px",
              }}>
              {title}
            </styled.h2>
            <VStack gap={2} alignItems="start">
              <styled.p
                css={{
                  color: "#1E1E1E",
                  fontFamily: "heading",
                  fontSize: "46px",
                  fontWeight: 700,
                  lineHeight: 1,
                }}>
                {price}
              </styled.p>
              <styled.div>
                <styled.p
                  css={{
                    color: "#000A3A",
                    fontSize: "20px",
                    lineHeight: "133%",
                    opacity: 0.8,
                  }}>
                  {priceUnit}
                </styled.p>
                <styled.p
                  css={{
                    color: "#8C60FA",
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: "133%",
                    opacity: 0.8,
                  }}>
                  {priceTip}
                </styled.p>
              </styled.div>
            </VStack>

            {features.length > 0 && (
              <VStack
                alignItems="start"
                gap={{ base: "4px", lg: "8px" }}
                css={{
                  color: "#000A3A",
                  fontSize: { base: "12px", lg: "20px" },
                  lineHeight: "133%",
                  opacity: 0.8,
                }}>
                {features.map((feature, i) => (
                  <FeatureListItem key={feature + i}>{feature}</FeatureListItem>
                ))}
              </VStack>
            )}

            {cta && (
              <Button
                variant="filled"
                fillColor="secondary"
                color="white"
                size="medium"
                // href={getHref(cta)}
              >
                {cta.text}
              </Button>
            )}
          </VStack>
        </Grid>
      </Container>
    </styled.div>
  )
}

const FeatureListItem = ({ children }) => (
  <HStack alignItems="start" gap={1}>
    <styled.svg
      m={1}
      flex="0"
      minW="max-content"
      hideBelow="lg"
      xmlns="http://www.w3.org/2000/svg"
      width="17px"
      height="17px"
      viewBox="0 0 17 17"
      fill="none">
      <rect width="17" height="17" rx="8.5" fill="#8C60FA" />
      <path
        d="M7.02474 11.8167C6.87159 11.8167 6.73121 11.7657 6.61634 11.6508L4.07659 9.11108C3.84687 8.88135 3.84687 8.524 4.07659 8.29428C4.30632 8.06455 4.66367 8.06455 4.8934 8.29428L7.03751 10.4256L12.1298 5.34612C12.3595 5.1164 12.7169 5.1164 12.9466 5.34612C13.1763 5.57585 13.1763 5.9332 12.9466 6.16293L7.44591 11.6508C7.31828 11.7657 7.1779 11.8167 7.02474 11.8167Z"
        fill="white"
      />
    </styled.svg>
    <styled.svg
      m={1}
      minW="max-content"
      hideFrom="lg"
      xmlns="http://www.w3.org/2000/svg"
      width="10px"
      height="11px"
      viewBox="0 0 10 11"
      fill="none">
      <rect y="0.0722656" width="10" height="10" rx="5" fill="#8C60FA" />
      <path
        d="M4.13243 7.02332C4.04234 7.02332 3.95976 6.99329 3.8922 6.92572L2.39822 5.43175C2.26309 5.29662 2.26309 5.08641 2.39822 4.95128C2.53336 4.81615 2.74356 4.81615 2.8787 4.95128L4.13994 6.20501L7.13539 3.21707C7.27052 3.08194 7.48073 3.08194 7.61586 3.21707C7.751 3.35221 7.751 3.56241 7.61586 3.69755L4.38018 6.92572C4.3051 6.99329 4.22252 7.02332 4.13243 7.02332Z"
        fill="white"
      />
    </styled.svg>
    <p>{children}</p>
  </HStack>
)

export default PricingShowcase
export type { Props as PricingShowcaseProps }
